/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import { SearchField, Item, Menu } from "@adobe/react-spectrum";
import {
  RankedTester,
  rankWith,
  RendererProps,
  uiTypeIs,
} from "@jsonforms/core";
import { useJsonForms, withJsonFormsLayoutProps } from "@jsonforms/react";
import React, { useState } from "react";

import {
  getHeaders,
  getBaseUrl,
  getEndpointName,
} from "@apollo/jsonforms-react-spectrum-renderers/src/util/commonUtils";
import "./css/SearchRenderer.scss";

// Lodash
import { get } from "lodash";

// Redux
import { useSelector } from "react-redux";

// Utils
import { evaluateContextRule, extractPath } from "../utils/commonUtils";

export const searchRendererTester: RankedTester = rankWith(
  4,
  uiTypeIs("SearchField")
);

function SearchRenderer({ uischema, visible, enabled }: RendererProps) {
  const [currentText, setCurrentText] = useState({ text: "", value: "" });
  const [listData, setListData] = useState<[]>([]);
  const [isSelected, setIsSelected] = React.useState(false);
  const context = useJsonForms();
  const spectrumProps = uischema.options?.spectrumProps;
  const style = uischema.options?.style;
  let baseUrl = uischema.options?.baseUrl;
  const dataPath = uischema.options?.dataPath;
  const searchLabel = uischema.options?.searchLabel;
  const searchKey = uischema.options?.searchKey;
  const endpointPath = uischema.options?.endpointPath;
  const headers = getHeaders(context?.config?.apiHeaders);
  const searchCallback =
    context?.config?.lifecycleHooks?.onSuccessCallbacks?.searchField;

  const onSelectValue = searchCallback?.onSelectValue;
  if (onSelectValue) {
    onSelectValue({
      isSelected,
      setIsSelected,
      currentText,
      setCurrentText,
      setListData,
    });
  }

  headers.append("content-type", "application/json;charset=UTF-8");

  const getSearchList = (data: any) => {
    let searchData = data;
    const searchList: any = [];
    if (dataPath) {
      searchData = get(data, dataPath, []);
    }
    if (searchData) {
      searchData.forEach((element: any) => {
        if (element != null && element.constructor.name === "Object") {
          const searchItem = get(element, searchKey, "");
          const label = get(element, searchLabel, "");
          searchList.push({ text: label, value: searchItem });
        } else {
          searchList.push({ text: element, value: element });
        }
      });
      setListData(searchList);
    }
  };
  React.useEffect(() => {
    const getSearchData = setTimeout(() => {
      if (!isSelected) {
        if (baseUrl && currentText.text) {
          const config = context?.config || {};
          if (endpointPath) {
            const pathKey = getEndpointName(endpointPath, config);
            const endpoint = get(config, pathKey, "");
            baseUrl = endpoint + baseUrl;
          }
          const urlvalue = getBaseUrl(baseUrl, {
            value: currentText.text.trim(),
          });
          document.getElementsByTagName("body")[0].style.cursor = "progress";
          fetch(urlvalue, {
            method: "GET",
            headers,
          })
            .then((res) => res.json())
            .then((data) => {
              if (data) {
                setListData([]);
                getSearchList(data);
              }
            })
            .finally(() => {
              document.getElementsByTagName("body")[0].style.cursor = "default";
            });
        }
      }
    }, 300);
    return () => clearTimeout(getSearchData);
  }, [currentText.text]);

  const changeHandler = (val: any) => {
    if (val) {
      setCurrentText({ text: val, value: "" });
    } else {
      setCurrentText({ text: val, value: "" });
      setListData([]);
    }
  };
  const onSelectHandler = (key: any) => {
    const selectedData: any = listData.filter(
      (item: any) => item.value === key
    )[0];
    setCurrentText(selectedData);
    setListData([]);
    setIsSelected(true);
  };
  React.useEffect(() => {
    if (!isSelected) {
      setListData([]);
      setCurrentText({ text: "", value: "" });
    }
  }, [isSelected]);

  React.useEffect(() => {
    if (currentText?.text === "" && listData.length > 0) {
      setListData([]);
    }
  }, [currentText, listData]);

  // Determine if the component should be visible and enabled
  let componentVisibility = visible;
  let componentEnabled = enabled;

  // If there is a context rule, evaluate it and use that visibility/enabled rule instead.
  if (uischema.options?.contextRule) {
    // grab the scope from the contextRule
    const scope = extractPath(uischema.options?.contextRule?.condition?.scope);
    // get the redux store state based on the scope provided
    const reduxState = useSelector((state: any) =>
      get(state?.reduxState, scope)
    );
    const contextRuleValidation = evaluateContextRule(
      uischema.options?.contextRule,
      reduxState
    );
    componentVisibility = contextRuleValidation?.visible;
    componentEnabled = contextRuleValidation?.enabled;
  }

  return (
    <>
      <SearchField
        data-testid="searchField"
        {...spectrumProps}
        UNSAFE_style={style}
        onClear={() => {
          setIsSelected(false);
        }}
        onChange={changeHandler}
        value={currentText.text}
        isDisabled={!componentEnabled}
        isHidden={!componentVisibility}
      />

      {currentText.text !== "" ? (
        <div
          className="listItems"
          style={
            listData.length <= 3
              ? { height: listData.length === 0 ? "0px" : "auto" }
              : { height: "104px" }
          }
        >
          <div className="containerul">
            <Menu
              maxWidth={spectrumProps.maxWidth}
              items={listData}
              onAction={(key: any) => onSelectHandler(key)}
            >
              {(item: any) => <Item key={item.value}>{item.text}</Item>}
            </Menu>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default withJsonFormsLayoutProps(SearchRenderer);
